/**
 * 창 관리 API(Window Management API)로 USB-C DisplayPort 외부 화면으로 연결된 XREAL 글래스를
 * 감시합니다. 글래스가 연결되어 있는 동안 재생 바의 VR 버튼을 켜고, 빠지면 회색으로 끕니다.
 *
 * 글래스는 EDID 이름으로 자신을 알립니다(시험 노트북에서 "XREAL One Pro", 2026-10-19).
 * ScreenDetailed.label 로 읽습니다. Chrome/Edge 데스크톱 전용이며, 그 밖(Safari, Firefox,
 * 모바일)에서는 'unsupported' 상태로 버튼이 꺼진 채 남습니다.
 *
 * 권한: getScreenDetails() 는 사이트별 "window-management" 권한이 필요합니다. 이미 허용됐으면
 * 조용히 시작합니다. 아직 묻지 않았으면('prompt') 호출 쪽이 클릭(VR 버튼)에서 request() 를
 * 부르거나 외부 화면이 처음 연결될 때까지(screen.isExtended 변화 - 아래 'change' 처리) 기다립니다.
 * 거부됐으면 사이트 설정에서 허용할 때까지 꺼져 있고, 허용하면 새로고침 없이 반영합니다.
 *
 * 창 위치(2026-10-30): ScreenDetails.currentScreen 과 'currentscreenchange' 로
 * 이 창이 지금 글래스 화면에 놓여 있는지(onGlasses)도 알려 줍니다. VR 버튼을 눌렀는지와
 * 상관없이 창이 글래스 화면에 있으면 글래스 모드를 켜기 위함입니다(안경 재연결 뒤 Windows 가
 * 창을 글래스 화면으로 되돌리거나, 창을 끌어다 놓은 경우 포함).
 */

interface ScreenDetailedLike extends EventTarget {
    readonly label: string;
    readonly isPrimary?: boolean;
}

interface ScreenDetailsLike extends EventTarget {
    readonly screens: ScreenDetailedLike[];
    readonly currentScreen?: ScreenDetailedLike;
}

type GlassesScreen = ScreenDetailedLike;
type GlassesPermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

interface GlassesWatch {
    /** 권한을 요청하고 감시 시작 - 클릭 처리 안에서 호출 */
    request: () => Promise<void>;
    /** 창이 놓인 화면을 다시 확인해 바뀌었으면 알림(전체 화면 변화 뒤 등) */
    refresh: () => void;
    /** PC 쪽 화면(글래스가 아닌 화면, 주 화면 우선) - 글래스 화면에서 PC 로 돌아갈 때 */
    pcScreen: () => ScreenDetailedLike | null;
}

const GLASSES_LABEL = /xreal|nreal/i;

const isGlasses = (s: ScreenDetailedLike | null | undefined) => !!s && GLASSES_LABEL.test(s.label ?? '');

const watchGlassesScreen = (onChange: (screen: GlassesScreen | null, permission: GlassesPermission, onGlasses: boolean) => void): GlassesWatch => {
    const getScreenDetails = (window as any).getScreenDetails as (() => Promise<ScreenDetailsLike>) | undefined;
    if (typeof getScreenDetails !== 'function') {
        onChange(null, 'unsupported', false);
        return { request: async () => {}, refresh: () => {}, pcScreen: () => null };
    }

    let details: ScreenDetailsLike | null = null;
    let permission: GlassesPermission = 'prompt';
    let lastScreen: GlassesScreen | null | undefined;
    let lastPermission: GlassesPermission | undefined;
    let lastOnGlasses: boolean | undefined;
    const emit = () => {
        const found = details?.screens.find(s => isGlasses(s)) ?? null;
        // 창이 지금 글래스 화면에 있는가
        const onGlasses = !!found && isGlasses(details?.currentScreen);
        if (found !== lastScreen || permission !== lastPermission || onGlasses !== lastOnGlasses) {
            lastScreen = found;
            lastPermission = permission;
            lastOnGlasses = onGlasses;
            onChange(found, permission, onGlasses);
        }
    };

    let starting: Promise<void> | null = null;
    const start = () => {
        if (details) {
            return Promise.resolve();
        }
        starting ??= getScreenDetails.call(window).then((d) => {
            details = d;
            permission = 'granted';
            d.addEventListener('screenschange', emit);
            d.addEventListener('currentscreenchange', emit);
            emit();
        }).catch(async (error: unknown) => {
            console.log('[ftgsDebug] glasses detection: window-management permission not granted:', error);
            // 클릭 없는 자동 요청은 사용자에게 묻지도 않고 거절될 수 있음 - 거부로 단정하지 않고
            // 실제 상태를 다시 읽어, 클릭으로 요청하는 길은 남겨 둠
            const status = await navigator.permissions?.query({ name: 'window-management' as PermissionName }).catch((): null => null);
            permission = status?.state === 'prompt' ? 'prompt' : 'denied';
            emit();
        }).finally(() => {
            starting = null;
        });
        return starting;
    };

    // 연결 시 자동 요청: 권한이 없을 때 브라우저가 주는 신호는 window.screen 의 'change' 뿐이고,
    // screen.isExtended 가 바뀔 때(첫 외부 화면 연결) 옵니다. 그때 묻습니다(이미 외부 모니터가
    // 있는 상태에서 화면을 더하면 바뀌지 않으므로 그 경우는 VR 버튼 클릭이 맡음).
    (screen as unknown as EventTarget).addEventListener?.('change', () => {
        if (permission === 'prompt' && (screen as any).isExtended) {
            start();
        }
    });

    emit();
    navigator.permissions?.query({ name: 'window-management' as PermissionName }).then((status) => {
        const apply = () => {
            if (status.state === 'granted') {
                start();
            } else {
                permission = status.state as GlassesPermission;
                emit();
            }
        };
        apply();
        // 나중에 사이트 설정에서 허용·차단해도 새로고침 없이 반영
        status.addEventListener('change', apply);
    }).catch(() => {
        // 이 브라우저가 권한 이름을 모름: request() 는 그래도 동작
    });

    const pcScreen = () => {
        const others = details?.screens.filter(s => !isGlasses(s)) ?? [];
        return others.find(s => s.isPrimary) ?? others[0] ?? null;
    };

    return { request: start, refresh: emit, pcScreen };
};

export { watchGlassesScreen };
export type { GlassesScreen, GlassesPermission };
